/**
 * The pure track-selection brain for the background music (ADR-0146, duel-combat
 * override): given the login {@link ./playlist.Playlist} and a client-local "in a duel"
 * flag, it decides **which track id should be playing right now**. Kept Web-Audio-free
 * and separate from the imperative {@link ./musicPlayer.MusicPlayer} - exactly the split
 * `Playlist` already keeps from the synth shell - so the override's engage/revert logic,
 * its idempotency, its login-cursor preservation across a duel, and its graceful degrade
 * when the combat track isn't baked are all unit-tested in plain node while the untested
 * shell (SpessaSynth, jsdom can't run it) stays a thin driver.
 *
 * The rule is deliberately trivial and drives itself off **local** duel state only: the
 * local player in a live duel ⇒ the looping combat track; otherwise ⇒ the login playlist.
 * Because both duelists' clients read their *own* replicated combat stance (see
 * `main.ts`), each switches independently and no other client's music is touched - there
 * is no networked music state. Every duel end path (win, loss, draw, decline/timeout,
 * disconnect, and a storm cancelling the duel) collapses to the same thing: the local
 * combat stance drops, so `setCombat(false)` reverts to the playlist - no per-path code.
 *
 * **Graceful absence.** The director takes an `isAvailable(id)` predicate (the player
 * passes "is this track baked?"). When the combat track isn't baked (a dev/test/CI host
 * with only the silent stub - or even that missing), engaging combat resolves to the
 * login track instead of a silent gap, and `setCombat` reports "no reload needed", so the
 * login music simply plays on. The same predicate skips any unbaked login entry.
 *
 * **Resume-on-revert semantics** (decided, ADR-0146): the login playlist cursor is *not*
 * advanced while combat is engaged, so reverting replays the track the cursor points at
 * (the one that was interrupted) from the top. A duel is short; restarting one login
 * track is the least surprising behavior and needs no "resume mid-track" bookkeeping.
 */
import { Playlist } from './playlist';
import { ARENA_INTENSITY_TIERS, arenaRemixTrackId } from '../sim/content';
import { ARENA_ENDLESS_STAGE_INTERVAL_TICKS } from '../sim/arena';

/** The registered {@link ../sim/content.music} track the duel-combat override plays. */
export const COMBAT_TRACK_ID = 'combat1';

/**
 * The registered {@link ../sim/content.music} track the Blackthorn's Storm override plays
 * (PRD #289 storm fix): a dungeon dirge that takes over for the whole event, the sibling of
 * {@link COMBAT_TRACK_ID}. It **outranks** the combat track (a storm cancels every duel, so
 * they never actually overlap - the precedence is just belt-and-suspenders).
 */
export const STORM_TRACK_ID = 'dungeon2';

/**
 * The default single {@link ../sim/content.music} track the **Arena run** override plays
 * when no arena playlist is supplied (Arena Slice E, PRD #334 / ADR-0177). Live, the
 * player passes the whole {@link ../sim/content.ARENA_PLAYLIST} (ADR-0210) and the override
 * *rotates* it instead of looping one track; this constant is only the test/opts default.
 * (`combat2` since the intensity-remix change retired `combat3` from the game.)
 * The arena override **outranks both** duel and storm - a runner is outside the shared
 * world, so they can be in neither (ADR-0176's entry gates); belt-and-suspenders.
 */
export const ARENA_TRACK_ID = 'combat2';

/**
 * The **arena music intensity clock**: how many elapsed run ticks each intensity tier
 * lasts before the mix steps up to the next remix. Deliberately the horde's own
 * escalation cadence ({@link ../sim/arena.ARENA_ENDLESS_STAGE_INTERVAL_TICKS}, 100s),
 * so the music intensifies exactly as the roster does: the base `.mid` for stage 1,
 * `_2` from stage 2 (1:40), up to `_5` from stage 5 (6:40) onward - the endless
 * escalation past that stays on the top remix.
 */
export const ARENA_TIER_INTERVAL_TICKS = ARENA_ENDLESS_STAGE_INTERVAL_TICKS;

/**
 * The intensity tier (`1..ARENA_INTENSITY_TIERS`) the arena mix should sit at after
 * `elapsedTicks` of the run - pure, driven by `main.ts` off every active
 * `ArenaRunStatus` push. Tier 1 (the base `.mid`) at run start, one step per
 * {@link ARENA_TIER_INTERVAL_TICKS}, clamped at the top remix. Monotonic because the
 * run clock is.
 */
export function arenaMusicTier(elapsedTicks: number): number {
  if (!Number.isFinite(elapsedTicks) || elapsedTicks < 0) return 1;
  return Math.min(ARENA_INTENSITY_TIERS, 1 + Math.floor(elapsedTicks / ARENA_TIER_INTERVAL_TICKS));
}

/**
 * The one-shot **victory sting** track (ADR-0210): played once (not looped, not in any
 * playlist) by {@link ../audio/musicPlayer.MusicPlayer.playVictory} when a new all-time
 * Arena high score is set, reverting to whatever was playing when it ends.
 */
export const VICTORY_TRACK_ID = 'victory';

/** Options for {@link MusicDirector}; all default so the player passes only what it needs. */
export interface MusicDirectorOptions {
  /** The combat-override track id (defaults to {@link COMBAT_TRACK_ID}). */
  combatTrackId?: string;
  /** The storm-override track id (defaults to {@link STORM_TRACK_ID}). */
  stormTrackId?: string;
  /**
   * The Arena-run-override **playlist** the override rotates through (ADR-0210),
   * defaulting to the single {@link ARENA_TRACK_ID} (the pre-playlist behaviour).
   * Live, the player passes {@link ../sim/content.ARENA_PLAYLIST}.
   */
  arenaTracks?: readonly string[];
  /**
   * Whether a track id has a baked (or stubbed) `.mid` available to play. Defaults to
   * "everything is available"; the player wires this to its loaded-tracks map so an
   * unbaked track is skipped (login) or degrades to the playlist (combat/storm).
   */
  isAvailable?: (id: string) => boolean;
}

export class MusicDirector {
  private readonly login: Playlist;
  private readonly arena: Playlist;
  private readonly combatTrackId: string;
  private readonly stormTrackId: string;
  private readonly isAvailable: (id: string) => boolean;
  private combatActive = false;
  private stormActive = false;
  private arenaActive = false;
  // The arena intensity tier (1 = the base .mid, 2..ARENA_INTENSITY_TIERS = the mp3
  // remixes). Recorded by setArenaTier but only *consulted* when a track loads, so a
  // mid-song tier-up is heard at the next track boundary (the at-track-end semantics).
  private arenaTier = 1;
  // Whether the run's base .mid has already played through once. A run opens on the mid
  // (tier 1); the moment it finishes it hands off to the remixes and the mid never
  // returns for the rest of the run (ADR-0246): once true, the *effective* tier floors
  // at 2 (the first remix), regardless of the intensity clock. This is the "the mid only
  // ever plays once" rule - the clock still climbs tiers 3..N over time from that floor.
  private arenaMidPlayed = false;

  constructor(loginTracks: readonly string[], opts: MusicDirectorOptions = {}) {
    this.login = new Playlist(loginTracks);
    this.arena = new Playlist(opts.arenaTracks ?? [ARENA_TRACK_ID]);
    this.combatTrackId = opts.combatTrackId ?? COMBAT_TRACK_ID;
    this.stormTrackId = opts.stormTrackId ?? STORM_TRACK_ID;
    this.isAvailable = opts.isAvailable ?? (() => true);
  }

  /**
   * Advance `playlist` over any unavailable entry - at most one full lap - and
   * return the first available id, or `undefined` if none of them are baked. A
   * query with a side effect (it may move the cursor to skip an unbaked entry),
   * matching the login skip loop; idempotent once the cursor rests on a playable one.
   */
  private firstAvailable(playlist: Playlist): string | undefined {
    for (let i = 0; i < playlist.length; i++) {
      const id = playlist.current();
      if (id && this.isAvailable(id)) return id;
      playlist.advance();
    }
    return undefined;
  }

  /**
   * The tier the arena mix should resolve at right now: the intensity-clock tier
   * ({@link setArenaTier}), floored at **2** once the run's base `.mid` has played
   * through once ({@link arenaMidPlayed}) so the mid never returns mid-run (ADR-0246).
   * Before the mid finishes the floor is 1, so the run always opens on the mid.
   */
  private effectiveArenaTier(): number {
    return Math.max(this.arenaTier, this.arenaMidPlayed ? 2 : 1);
  }

  /**
   * The best available id for arena track `base` at the current intensity: the
   * `_tier` remix if baked, degrading **downward** through the lower remixes to the
   * base `.mid` (a partially-baked remix set never silences the run - it just plays
   * the closest lower intensity), or `undefined` when not even the mid is baked.
   */
  private arenaTierCandidate(base: string): string | undefined {
    for (let tier = this.effectiveArenaTier(); tier >= 1; tier--) {
      const id = arenaRemixTrackId(base, tier);
      if (this.isAvailable(id)) return id;
    }
    return undefined;
  }

  /**
   * Resolve the arena override's track: **the run's one song** (where the cursor
   * landed at engage) at the current intensity tier (see {@link arenaTierCandidate}).
   * The cursor only ever advances past a song with nothing playable at any tier - at
   * most one full lap, like the login skip loop - so with a normal bake a whole run
   * is thematic: one song, mid first, its remixes as the ramp climbs.
   */
  private arenaResolve(): string | undefined {
    for (let i = 0; i < this.arena.length; i++) {
      const base = this.arena.current();
      if (base) {
        const id = this.arenaTierCandidate(base);
        if (id) return id;
      }
      this.arena.advance();
    }
    return undefined;
  }

  /** Whether the arena playlist has any playable track at the current intensity
   * (counting the downward degrade) - a cursor-free scan (for the
   * override-owns-the-mix guards and {@link setArena}'s reload decision). */
  private arenaHasAvailable(): boolean {
    return this.arena.ids.some((base) => this.arenaTierCandidate(base) !== undefined);
  }

  /** Whether the duel-combat override is currently engaged. */
  get inCombat(): boolean {
    return this.combatActive;
  }

  /** Whether the Blackthorn's Storm override is currently engaged. */
  get inStorm(): boolean {
    return this.stormActive;
  }

  /** Whether the Arena run override is currently engaged (Arena Slice E, ADR-0177). */
  get inArena(): boolean {
    return this.arenaActive;
  }

  /** The login track the cursor points at (unaffected by the override); for tests. */
  get loginTrack(): string | undefined {
    return this.login.current();
  }

  /** The arena track the arena cursor points at (unaffected by engage state); for tests. */
  get arenaTrack(): string | undefined {
    return this.arena.current();
  }

  /**
   * The track id to load and play right now, or `undefined` if nothing is available. In
   * combat the combat track takes over **iff it is available**; otherwise (or when it
   * isn't baked) the login playlist, advanced forward over any unbaked entry - at most
   * one full lap, giving up quietly if none of the login tracks are available.
   *
   * Note this may advance the login cursor to skip unbaked entries (a query with a side
   * effect, matching the player's existing skip-forward loop); it never advances past an
   * available track, so it is idempotent once the cursor rests on a playable one.
   */
  resolve(): string | undefined {
    // Arena outranks storm outranks combat outranks login (each only when its track is
    // actually baked). A runner can be in neither a duel nor a storm (ADR-0176), so the
    // top rank is belt-and-suspenders like storm-over-combat. The arena override is a
    // rotating playlist (ADR-0210) - resolve to its first available track.
    if (this.arenaActive) {
      const id = this.arenaResolve();
      if (id) return id;
    }
    if (this.stormActive && this.isAvailable(this.stormTrackId)) return this.stormTrackId;
    if (this.combatActive && this.isAvailable(this.combatTrackId)) return this.combatTrackId;
    return this.firstAvailable(this.login);
  }

  /**
   * Engage or disengage the duel-combat override. Idempotent: setting the state it is
   * already in is a no-op returning `false`. Returns **whether the player should reload
   * the current track** - i.e. `true` only when the switch actually changes what plays,
   * which is exactly "the combat track is available". When it isn't baked, engaging or
   * leaving combat returns `false` so the login music plays on undisturbed (the graceful
   * degrade). The login cursor is deliberately left untouched (see resume semantics).
   */
  setCombat(active: boolean): boolean {
    if (active === this.combatActive) return false;
    this.combatActive = active;
    // No reload while a higher-ranked override already owns the mix (arena outranks
    // storm outranks combat): the switch changes nothing audible, so report "no reload"
    // and let the owning track play on.
    if (this.arenaActive && this.arenaHasAvailable()) return false;
    if (this.stormActive && this.isAvailable(this.stormTrackId)) return false;
    return this.isAvailable(this.combatTrackId);
  }

  /**
   * Engage or disengage the Blackthorn's Storm override (PRD #289 storm fix), the twin of
   * {@link setCombat}. Idempotent (setting the current state returns `false`). Returns
   * whether the player should reload the current track - `true` only when the switch changes
   * what plays: engaging when the storm track is baked (it now outranks combat/login), or
   * leaving when it was playing (revert to combat/login). When the storm track isn't baked it
   * degrades to leaving whatever plays alone. The login cursor is left untouched, so the
   * interrupted login track replays from the top on revert (the combat-override semantics).
   */
  setStorm(active: boolean): boolean {
    if (active === this.stormActive) return false;
    this.stormActive = active;
    // No reload while an Arena run owns the mix (arena outranks storm) - can't happen
    // live (a runner receives no storm edge, ADR-0176), belt-and-suspenders.
    if (this.arenaActive && this.arenaHasAvailable()) return false;
    return this.isAvailable(this.stormTrackId);
  }

  /**
   * Engage or disengage the **Arena run** override (Arena Slice E, PRD #334 / ADR-0177),
   * the third sibling of {@link setCombat}/{@link setStorm} and the top of the precedence
   * order. Driven by `main.ts` off the run-active edge of the owner-only
   * `ArenaRunStatus` pushes, so only the runner's own client switches. Idempotent
   * (setting the current state returns `false`); returns whether the player should
   * reload the current track - `true` only when the switch changes what plays. When the
   * arena track isn't baked it degrades to leaving whatever plays alone (never a silent
   * gap). The login cursor is left untouched, so the interrupted login track replays
   * from the top on revert (the combat/storm-override semantics).
   */
  setArena(active: boolean): boolean {
    if (active === this.arenaActive) return false;
    this.arenaActive = active;
    // Every run opens on the base .mid (tier 1) - the intensity ramp is per-run, so
    // engaging resets whatever tier the previous run climbed to. (main.ts also pushes
    // the tier from elapsedTicks, which is 0 at run start; belt-and-suspenders.) The
    // mid-played latch resets too, so the fresh run opens on the mid again (ADR-0246).
    if (active) {
      this.arenaTier = 1;
      this.arenaMidPlayed = false;
    }
    // Reload iff the arena playlist has *something* to play (engage → switch to it;
    // disengage → revert from it). When nothing in it is baked it degrades to
    // leaving the current music alone (the graceful path), exactly like combat/storm.
    return this.arenaHasAvailable();
  }

  /**
   * Record the arena **intensity tier** (`1..ARENA_INTENSITY_TIERS`, clamped) - driven
   * by `main.ts` off {@link arenaMusicTier}(elapsedTicks) on every active
   * `ArenaRunStatus` push. Deliberately returns nothing and never asks for a reload:
   * a tier change is heard **at the next track boundary** (the following
   * {@link songEnded} resolves at the new tier), not as a mid-song hard cut - and the
   * run stays on its one song throughout (ADR-0232), so a tier-up means "the same
   * song's next remix"; the mid only ever plays at run start.
   */
  setArenaTier(tier: number): void {
    this.arenaTier = Math.min(ARENA_INTENSITY_TIERS, Math.max(1, Math.floor(tier)));
  }

  /** The recorded arena intensity tier; for tests. */
  get arenaIntensity(): number {
    return this.arenaTier;
  }

  /**
   * Jump the arena playlist cursor to `index` (wrapping) - this picks **the run's
   * song** (ADR-0232): the {@link ../audio/musicPlayer.MusicPlayer} calls it with a
   * random index on each arena engage, and the whole run then sticks to that song
   * (its mid, then its remixes). Kept out of {@link setArena} so the pure director
   * stays deterministic and the randomness lives in the shell.
   */
  reseedArena(index: number): void {
    this.arena.seek(index);
  }

  /**
   * A track finished playing. Under the storm/combat override that single track loops
   * (the cursors stay put); under the **arena** override the run **stays on its one
   * song** (ADR-0232) - the cursor stays put and {@link resolve} re-picks the same
   * song. The base `.mid` plays through exactly **once** (ADR-0246): the first arena
   * `songEnded` latches {@link arenaMidPlayed}, flooring the effective tier at the
   * first remix, so from the mid's end onward the remixes take over and each remix
   * loops until the ramp climbs; otherwise the login playlist advances. Returns the
   * {@link resolve}d id to play next.
   */
  songEnded(): string | undefined {
    if (this.arenaActive) {
      // The run always opens on the mid, so the first arena songEnded is the mid
      // finishing; latch so it hands off to the remixes and never returns this run.
      this.arenaMidPlayed = true;
    } else if (!this.stormActive && !this.combatActive) {
      this.login.advance();
    }
    return this.resolve();
  }
}
